import React, { useState } from 'react';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Assessment as ReportsIcon,
  Timeline as MovementsIcon,
  ReceiptLong as RegisterIcon,
  Checklist as StockCountIcon,
  Warehouse as WarehouseIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useVenue } from '../../../../context/VenueContext';
import ReportsHelpModal from '../../../../components/venue/ReportsHelpModal';
import PageHeader from '../../../../components/venue/PageHeader';

/**
 * REPORTING-CENTER.1 — venue Reports page.
 *
 * A card grid of vetted venue reports (TRIM.1: v1 shows only the three
 * report cards; the live views and ownership/POS surfaces keep their routes
 * but are no longer advertised here). Visible to all venue users.
 */

interface ReportCard {
  key: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  path: string;
  badge?: string;
}

const ReportsCenter: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { venueId } = useVenue();
  const [helpOpen, setHelpOpen] = useState(false);

  const base = `/admin/venues/${venueId}`;

  const cards: ReportCard[] = [
    {
      key: 'inventory-movements',
      title: 'Inventory Movements & Stock on Hand',
      description: 'Opening stock, every movement, and closing stock per item over any date range. Export: XLSX',
      icon: <MovementsIcon />,
      path: `${base}/reports/inventory-movements`,
      badge: 'New',
    },
    {
      key: 'transaction-register',
      title: 'Transaction Register',
      description: 'A finance-grade ledger of every movement, with In/Out columns and full filtering. Pick a single item for its Stock Card with a running balance. Export: XLSX & CSV',
      icon: <RegisterIcon />,
      path: `${base}/reports/transactions`,
      badge: 'New',
    },
    {
      key: 'warehouse-consumption',
      title: 'Warehouse Consumption by Venue',
      description: 'Who consumed a warehouse\u2019s stock, and how much. Answers the shared-cellar question the other reports cannot: a cross-venue sale is credited to the venue that poured it, so the venue administering the cellar can see who took what.',
      icon: <WarehouseIcon />,
      path: `${base}/reports/warehouse-consumption`,
      badge: 'New',
    },
    {
      key: 'cycle-report',
      title: 'Inventory Cycle Report',
      description: 'Explains the period between two posted stock counts, with expected-vs-counted variance per item. Export: XLSX — from a posted count’s page',
      icon: <StockCountIcon />,
      path: `${base}/stock-counts`,
    },
    {
      key: 'stock-counts',
      title: 'Stock Counts',
      description: 'Create, enter, and post physical stock counts. Export: XLSX — blank count worksheets',
      icon: <StockCountIcon />,
      path: `${base}/stock-counts`,
    },
  ];

  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <PageHeader
        icon={<ReportsIcon />}
        title="Reports"
        onHelp={() => setHelpOpen(true)}
        helpTitle="What lives here?"
        subtitle="Every report and live view for this venue in one place. All read-only."
      />

      {/* ── Card grid ──────────────────────────────────────────────────────── */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
          gap: 2,
        }}
      >
        {cards.map(card => (
          <Card
            key={card.key}
            sx={{
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
              transition: 'all 0.15s ease-in-out',
              '&:hover': { boxShadow: '0 6px 20px rgba(0,0,0,0.12)', transform: 'translateY(-2px)' },
            }}
          >
            <CardActionArea onClick={() => navigate(card.path)} sx={{ height: '100%' }}>
              <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1, height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ color: theme.palette.primary.main, display: 'flex' }}>{card.icon}</Box>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.25 }}>
                    {card.title}
                  </Typography>
                  {card.badge && (
                    <Chip
                      label={card.badge}
                      size="small"
                      color="primary"
                      sx={{ height: 20, fontSize: '0.68rem', fontWeight: 700, ml: 'auto' }}
                    />
                  )}
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
                  {card.description}
                </Typography>
              </CardContent>
            </CardActionArea>
          </Card>
        ))}
      </Box>

      <ReportsHelpModal open={helpOpen} onClose={() => setHelpOpen(false)} />
    </Box>
  );
};

export default ReportsCenter;
